'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "value", "addPropertiesToDroppedEvent", "lockSurfaceType"],
  _excluded2 = ["aria-labelledby"];
import * as React from 'react';
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { useCompositeListItem } from "../../base-ui-copy/composite/list/useCompositeListItem.mjs";
import { useCompositeListContext } from "../../base-ui-copy/composite/list/CompositeListContext.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { useEventCreation } from "../../internals/utils/useEventCreation.mjs";
import { getCalendarGridHeaderCellId } from "../../internals/utils/accessibility-utils.mjs";
import { useKeyboardEventCreation } from "../../internals/utils/useKeyboardEventCreation.mjs";
import { getNavigationTarget } from "../../internals/utils/getNavigationTarget.mjs";
import { useCalendarGridRootContext } from "../root/CalendarGridRootContext.mjs";
import { useCalendarGridDayRowContext } from "../day-row/CalendarGridDayRowContext.mjs";
import { useDayCellDropTarget } from "./useDayCellDropTarget.mjs";
import { CalendarGridDayCellContext } from "./CalendarGridDayCellContext.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
export const CalendarGridDayCell = /*#__PURE__*/React.forwardRef(function CalendarGridDayCell(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      value,
      addPropertiesToDroppedEvent,
      lockSurfaceType
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  const adapter = useAdapterContext();
  const {
    id: rootId,
    focusedCell,
    setFocusedCell,
    rowTypes,
    rowsPerType
  } = useCalendarGridRootContext();
  const {
    rowIndex
  } = useCalendarGridDayRowContext();
  const {
    ref: listItemRef,
    index
  } = useCompositeListItem();
  const {
    elementsRef
  } = useCompositeListContext();
  const dropTargetRef = useDayCellDropTarget({
    value,
    addPropertiesToDroppedEvent
  });
  const columnHeaderId = getCalendarGridHeaderCellId(rootId, index);
  const cellRef = React.useRef(null);
  const hasFocus = focusedCell?.rowType === 'day-grid' && focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === index;
  const getPlaceholderFields = () => ({
    surfaceType: 'day-grid',
    start: adapter.startOfDay(value),
    end: adapter.endOfDay(value),
    lockSurfaceType,
    resourceId: null
  });
  const eventCreationProps = useEventCreation(getPlaceholderFields);
  const triggerKeyboardCreation = useKeyboardEventCreation(getPlaceholderFields);

  // Apply DOM focus when this cell becomes the focused cell
  React.useEffect(() => {
    if (hasFocus && cellRef.current && !cellRef.current.contains(document.activeElement)) {
      cellRef.current.focus({
        preventScroll: true
      });
    }
  }, [hasFocus]);
  const handleKeyDown = event => {
    const target = getNavigationTarget(event.key, 'day-grid', rowIndex, index, {
      columnCount: elementsRef.current.length,
      rowTypes,
      rowsPerType
    });
    if (target) {
      event.preventDefault();
      setFocusedCell(target);
      return;
    }
    if (event.key === 'Enter' && event.target === event.currentTarget && triggerKeyboardCreation) {
      event.preventDefault();
      triggerKeyboardCreation();
    }
  };
  const handleFocus = event => {
    if (event.target === event.currentTarget) {
      setFocusedCell({
        rowType: 'day-grid',
        rowIndex,
        columnIndex: index
      });
    }
  };
  const contextValue = React.useMemo(() => ({
    index,
    hasFocus
  }), [index, hasFocus]);

  // Associate this cell with its column header, matching the pattern used by DayEvent and TimeEvent.
  // Any additional aria-labelledby passed by the styled layer (e.g., an "All day" row header) is appended.
  const {
      'aria-labelledby': elementAriaLabelledBy
    } = elementProps,
    restElementProps = _objectWithoutPropertiesLoose(elementProps, _excluded2);
  const ariaLabelledBy = [columnHeaderId, elementAriaLabelledBy].filter(Boolean).join(' ');
  const keyboardProps = {
    // All cells are always tabbable so Tab flows through: cell → events → next cell → events.
    // Arrow keys navigate programmatically via setFocusedCell, independent of tabIndex.
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus
  };
  const element = useRenderElement('div', componentProps, {
    ref: [forwardedRef, dropTargetRef, listItemRef, cellRef],
    props: [{
      role: 'gridcell',
      'aria-labelledby': ariaLabelledBy || undefined,
      'aria-colindex': index + 1
    }, keyboardProps, eventCreationProps, restElementProps]
  });
  return /*#__PURE__*/_jsx(CalendarGridDayCellContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridDayCell.displayName = "CalendarGridDayCell";